import { useEffect, useCallback, useRef } from "react";

/**
 * Warns users before navigating away when there are unsaved changes.
 * Uses the browser's native beforeunload event.
 */
export function useUnsavedChanges(isDirty: boolean) {
  useEffect(() => {
    if (!isDirty) return;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);
}

/**
 * Prevents double-submit on forms.
 * Returns { isSubmitting, guard } where guard wraps your submit fn.
 */
export function useSubmitGuard() {
  const submittingRef = useRef(false);

  const guard = useCallback(<T,>(fn: () => T): T | undefined => {
    if (submittingRef.current) return undefined;
    submittingRef.current = true;
    try {
      const result = fn();
      // If promise, wait for it
      if (result instanceof Promise) {
        (result as Promise<unknown>).finally(() => {
          submittingRef.current = false;
        });
      } else {
        // Sync — unlock after a short debounce to prevent rapid clicks
        setTimeout(() => { submittingRef.current = false; }, 600);
      }
      return result;
    } catch {
      submittingRef.current = false;
      return undefined;
    }
  }, []);

  return { isSubmitting: submittingRef, guard };
}
